import { ZEME_USER_LOCATION } from '@src/v3/constants/localStorage';
import { getItem, setItem } from '@src/v3/utils/localStorage';

/**
 * Cached location structure stored in localStorage
 */
export interface CachedLocation {
  lat: number;
  lng: number;
  timestamp: number;
}

/**
 * Response structure from geojs.io API
 */
interface GeoJsResponse {
  latitude: string;
  longitude: string;
}

/**
 * Location coordinates
 */
interface LocationCoords {
  lat: number;
  lng: number;
}

// Cache validity duration: 24 hours in milliseconds
const CACHE_VALIDITY_MS = 24 * 60 * 60 * 1000;

const getCachedLocationWithTimestamp = (): CachedLocation | null => {
  try {
    if (!isBrowser()) {
      return null;
    }

    const cached = getItem<CachedLocation>(ZEME_USER_LOCATION);
    if (!cached) {
      return null;
    }

    const now = Date.now();
    const age = now - cached.timestamp;

    return age < CACHE_VALIDITY_MS ? cached : null;
  } catch (error) {
    console.error('Error getting cached location:', error);
    return null;
  }
};

// In-flight location fetch promise to avoid duplicate requests
let locationPromise: Promise<LocationCoords | null> | null = null;

/**
 * Check if we're in a browser environment
 */
const isBrowser = (): boolean => {
  return typeof window !== 'undefined';
};

/**
 * Get cached location from localStorage if it exists and is still valid (< 24 hours old)
 * @returns Cached location coordinates or null if not found/expired
 */
export const getCachedLocation = (): LocationCoords | null => {
  const cached = getCachedLocationWithTimestamp();
  if (!cached) {
    return null;
  }

  return { lat: cached.lat, lng: cached.lng };
};

/**
 * Save location to localStorage with current timestamp
 * @param lat - Latitude
 * @param lng - Longitude
 */
export const setCachedLocation = (lat: number, lng: number): void => {
  const cached: CachedLocation = {
    lat,
    lng,
    timestamp: Date.now(),
  };

  try {
    if (!isBrowser()) {
      return;
    }

    setItem(ZEME_USER_LOCATION, cached);
  } catch (error) {
    console.error('Error caching location:', error);
  }
};

/**
 * Get location from browser's Geolocation API
 * @returns Promise resolving to location coordinates or null if failed/denied
 */
export const getLocationFromBrowser = (): Promise<LocationCoords | null> => {
  return new Promise((resolve) => {
    if (!isBrowser() || !navigator.geolocation) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      () => {
        // Permission denied or error - resolve with null
        resolve(null);
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: CACHE_VALIDITY_MS,
      }
    );
  });
};

/**
 * Get location from IP address using geojs.io API (CORS-friendly, no API key required)
 * @returns Promise resolving to location coordinates or null if failed
 */
export const getLocationFromIP = async (): Promise<LocationCoords | null> => {
  try {
    if (!isBrowser()) {
      return null;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 3000);

    const response = await fetch('https://get.geojs.io/v1/ip/geo.json', {
      signal: controller.signal,
    }).finally(() => window.clearTimeout(timeoutId));

    if (!response.ok) {
      return null;
    }

    const data: GeoJsResponse = await response.json();
    const lat = parseFloat(data.latitude);
    const lng = parseFloat(data.longitude);

    if (isNaN(lat) || isNaN(lng)) {
      return null;
    }

    return { lat, lng };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      console.error('Error getting location from IP:', error);
      return null;
    }
    console.error('Error getting location from IP:', error);
    return null;
  }
};

/**
 * Internal function to fetch location (without caching the promise)
 */
const fetchLocation = async (): Promise<LocationCoords | null> => {
  // Try browser geolocation
  const browserLocation = await getLocationFromBrowser();
  if (browserLocation) {
    setCachedLocation(browserLocation.lat, browserLocation.lng);
    return browserLocation;
  }

  // Fallback to IP-based geolocation
  const ipLocation = await getLocationFromIP();
  if (ipLocation) {
    setCachedLocation(ipLocation.lat, ipLocation.lng);
    return ipLocation;
  }

  // All methods failed
  return null;
};

/**
 * Get user location using the following strategy:
 * 1. Check for valid cached location (< 24 hours old)
 * 2. If a fetch is already in progress, wait for it
 * 3. Try browser geolocation API
 * 4. Fallback to IP-based geolocation via geojs.io
 * 5. Return null if all methods fail
 *
 * Successfully obtained locations are cached for 24 hours.
 *
 * @returns Promise resolving to location coordinates or null
 */
export const getUserLocation = async (): Promise<LocationCoords | null> => {
  // First, check for valid cached location
  const cached = getCachedLocation();
  if (cached) {
    return cached;
  }

  // If a fetch is already in progress, wait for it
  if (locationPromise) {
    return locationPromise;
  }

  // Start a new fetch and store the promise
  locationPromise = fetchLocation().finally(() => {
    // Clear the promise once complete (success or failure)
    locationPromise = null;
  });

  return locationPromise;
};

/**
 * Get user location record that includes the cached timestamp.
 * Falls back to `getUserLocation` if no valid cache exists.
 */
export const getUserLocationRecord =
  async (): Promise<CachedLocation | null> => {
    const cached = getCachedLocationWithTimestamp();
    if (cached) {
      return cached;
    }

    const location = await getUserLocation();
    if (!location) {
      return null;
    }

    return getCachedLocationWithTimestamp();
  };

/**
 * Prefetch user location in the background.
 * Call this early in the app lifecycle to warm up the location cache.
 * This prevents the first search request from being slow.
 */
export const prefetchLocation = (): void => {
  if (!isBrowser()) {
    return;
  }

  // Check if we already have a valid cached location
  const cached = getCachedLocation();
  if (cached) {
    return;
  }

  // Start fetching in the background (fire and forget)
  getUserLocation().catch(() => {
    // Silently ignore errors - location is optional
  });
};

/**
 * Get origin string for API requests in format "{lat},{lng}"
 * Returns undefined if location cannot be determined (to allow API call without origin)
 *
 * @returns Promise resolving to origin string or undefined
 */
export const getOriginString = async (): Promise<string | undefined> => {
  const location = await getUserLocation();

  if (!location) {
    return undefined;
  }

  return `${location.lat},${location.lng}`;
};
